/**
 * Country of residence, for the onboarding form.
 *
 * ## This is not the geo-block, and it must not be mistaken for one
 *
 * KEV is served to Australia only, and that is enforced at the edge by the
 * `AustraliaOnly` WAF rule pair (RIPK/api/cdk/lib/kev-edge-stack.ts) — one
 * blocks /api/* for non-AU viewers, the other 302s document requests to
 * /unavailable. That rule gates on WHERE THE REQUEST COMES FROM.
 *
 * This field asks something different: where the person LIVES. Those genuinely
 * differ — an Australian resident on holiday, someone here on a working visa —
 * and residence is the one that matters to the terms, which are governed by NSW
 * law and written for Australian consumers. So a non-AU answer is recorded
 * truthfully rather than refused: this is a declaration we keep, not a second
 * gate we enforce. Refusing here would also be enforcement the WAF has already
 * done better, in a place the user cannot edit.
 *
 * ## Why this list is a subset
 *
 * Deliberately. A 249-entry dropdown on a product that only reaches one country
 * is a worse form than a short one, and every entry past the plausible set is a
 * value we would store and never be able to interpret. AU is first because it is
 * the answer for effectively everyone who can load this page; the rest cover the
 * cases that actually occur — nearby countries, and the ones AU has the most
 * movement with. Add to it when a real user cannot answer honestly, not before.
 *
 * Codes are ISO-3166-1 alpha-2 and are stored upper-case (the API upper-cases
 * them again server-side regardless).
 */
export interface Country {
  code: string;
  name: string;
}

export const COUNTRIES: readonly Country[] = [
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "CA", name: "Canada" },
  { code: "CN", name: "China" },
  { code: "FJ", name: "Fiji" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "HK", name: "Hong Kong" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IE", name: "Ireland" },
  { code: "IT", name: "Italy" },
  { code: "JP", name: "Japan" },
  { code: "MY", name: "Malaysia" },
  { code: "NL", name: "Netherlands" },
  { code: "PH", name: "Philippines" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "SG", name: "Singapore" },
  { code: "ZA", name: "South Africa" },
  { code: "KR", name: "South Korea" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "TH", name: "Thailand" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "VN", name: "Vietnam" },
] as const;
